import {
  getSavedFilters,
  saveFilter,
  getFilterOptions,
} from '@/services/filters.service';
import {
  type FilterConfig,
  type FilterNegations,
  type FilterOption,
  type FilterValue,
  type DateRangeValue,
  type RangeValue,
  isDateRange,
  isRange,
  isSelect,
} from '@/types/filters.types';
import { isDateRangePresetKey, resolvePreset } from '@/lib/dateRangePresets';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useWithLeadSourceOptions } from '@/hooks/filters/leadSourceOptions';

function defaultForConfig(c: FilterConfig): FilterValue {
  if (c.type === 'daterange') return { from: '', to: '' } as DateRangeValue;
  if (c.type === 'range') return { min: c.min ?? 0, max: c.max ?? 120 } as RangeValue;
  return [] as string[];
}

/**
 * A daterange persists as `from,to,preset` — the legacy three-part form the
 * server stores verbatim and `v5/hooks/useFilters` already writes. The PRESET
 * KEY is the part that matters: without it a saved "Today" comes back as the
 * two literal dates it happened to resolve to, and still reads as those dates
 * tomorrow. The dates are written alongside it only so a row stays readable to
 * anything that ignores the third part.
 */
function serializeForPersist(_key: string, val: FilterValue): string {
  if (isDateRange(val)) return `${val.from},${val.to},${val.preset ?? ''}`;
  if (isRange(val)) return `${val.min},${val.max}`;
  if (isSelect(val)) return val.join(',') + ';;;;true';
  return '';
}

function deserializeFromPersist(config: FilterConfig, raw: string[]): FilterValue {
  // `serializeForPersist` always joins daterange/range into a single
  // comma-separated string, which the API stores as one row. So `raw` will
  // typically have length 1 here. Parse from the joined form to handle that
  // (and any legacy multi-row format) uniformly.
  if (config.type === 'daterange') {
    const parts = raw.join(',').split(',');
    // Re-resolve from the key rather than trusting the stored dates, so a
    // relative preset stays relative to now. Rows written before the preset was
    // persisted have no third part and fall through to their literal dates.
    const presetRaw = parts[2] ?? '';
    if (isDateRangePresetKey(presetRaw)) {
      return { ...resolvePreset(presetRaw), preset: presetRaw };
    }
    return { from: parts[0] ?? '', to: parts[1] ?? '' };
  }
  if (config.type === 'range') {
    const parts = raw.join(',').split(',');
    return { min: Number(parts[0]) || 0, max: Number(parts[1]) || 120 };
  }
  const plusSplit = raw.join(',').split(';;;;');
  const vals = plusSplit[0].split(',').filter(Boolean);
  return vals;
}

/**
 * Legacy semicolon wire format read by LeadCentralFilters::fromRequest():
 * `filtersArray` = `key:v1,v2;key2:v3`, `filterPlus` = `key:0;key2:1` where
 * 1 marks the filter NEGATIVE (NOT IN). Pass the sidebar's negations or every
 * toggle is sent as positive.
 */
export function buildApiFilterString(
  values: Record<string, FilterValue>,
  negations: FilterNegations = {},
): { filtersArray: string; filterPlus: string } {
  const parts: string[] = [];
  const plusParts: string[] = [];

  for (const [key, val] of Object.entries(values)) {
    if (isDateRange(val)) {
      if (val.from || val.to) parts.push(`daterange:${val.from},${val.to}`);
    } else if (isRange(val)) {
      if (val.min > 0 || val.max < 120)
        parts.push(`${key}:${val.min},${val.max}`);
    } else if (isSelect(val) && val.length > 0) {
      parts.push(`${key}:${val.join(',')}`);
      plusParts.push(`${key}:${negations[key] ? 1 : 0}`);
    }
  }

  return { filtersArray: parts.join(';'), filterPlus: plusParts.join(';') };
}

interface UseFiltersReturn {
  values: Record<string, FilterValue>;
  options: Record<string, FilterOption[]>;
  loading: boolean;
  setFilter: (key: string, value: FilterValue) => void;
  clearFilter: (key: string) => void;
  clearAll: () => void;
}

export function useFilters(
  pageUrl: string,
  configs: FilterConfig[],
): UseFiltersReturn {
  const [values, setValues] = useState<Record<string, FilterValue>>(() => {
    const init: Record<string, FilterValue> = {};
    for (const c of configs) init[c.key] = defaultForConfig(c);
    return init;
  });
  const [options, setOptions] = useState<Record<string, FilterOption[]>>({});
  const [loading, setLoading] = useState(true);
  const configsRef = useRef(configs);
  configsRef.current = configs;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const selectTypes = configs
          .filter((c) => c.type === 'select')
          .map((c) => c.key);

        const [savedResult, optionsResult] = await Promise.allSettled([
          getSavedFilters(pageUrl, '1'),
          selectTypes.length > 0
            ? getFilterOptions(selectTypes)
            : Promise.resolve({} as Record<string, FilterOption[]>),
        ]);

        if (cancelled) return;

        const optionsRes =
          optionsResult.status === 'fulfilled' ? optionsResult.value : null;
        const savedRes =
          savedResult.status === 'fulfilled' ? savedResult.value : null;

        if (optionsRes) setOptions(optionsRes);

        if (savedRes?.filters) {
          const restored: Record<string, FilterValue> = {};
          for (const c of configsRef.current) {
            const raw = savedRes.filters[c.key];
            if (raw && raw.length > 0) {
              restored[c.key] = deserializeFromPersist(c, raw);
            } else {
              restored[c.key] = defaultForConfig(c);
            }
          }
          setValues(restored);
        }
      } catch (err) {
        console.error('useFilters: failed to load', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [pageUrl, configs]);

  const persistFilter = useCallback(
    (key: string, val: FilterValue) => {
      const serialized = serializeForPersist(key, val);
      saveFilter({
        filterType: key,
        filterValue: serialized,
        pageUrl,
        viewType: '1',
      }).catch((err) =>
        console.error('useFilters: failed to persist', key, err),
      );
    },
    [pageUrl],
  );

  const setFilter = useCallback(
    (key: string, value: FilterValue) => {
      setValues((prev) => ({ ...prev, [key]: value }));
      persistFilter(key, value);
    },
    [persistFilter],
  );

  const clearFilter = useCallback(
    (key: string) => {
      const config = configsRef.current.find((c) => c.key === key);
      if (!config) return;
      const def = defaultForConfig(config);
      setValues((prev) => ({ ...prev, [key]: def }));
      persistFilter(key, def);
    },
    [persistFilter],
  );

  const clearAll = useCallback(() => {
    const cleared: Record<string, FilterValue> = {};
    for (const c of configsRef.current) {
      const def = defaultForConfig(c);
      cleared[c.key] = def;
      persistFilter(c.key, def);
    }
    setValues(cleared);
  }, [persistFilter]);

  // Lead source is served by the shared hierarchy builder instead of the bulk
  // `/filters/options` payload, so every page using this hook gets the same
  // category/source tree as the Plans list. Only fetched when the page actually
  // declares the filter.
  const hasLeadSource = configs.some((c) => c.key === 'lead-source');
  const optionsWithLeadSource = useWithLeadSourceOptions(options, {
    enabled: hasLeadSource,
  });

  return {
    values,
    options: optionsWithLeadSource,
    loading,
    setFilter,
    clearFilter,
    clearAll,
  };
}
